import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory, useLocation } from "react-router-dom";
import { toast } from 'react-toastify';

import { CompleteUserForm, Loading, ModalAuth } from 'components/standalone';
import { CheckoutState } from 'store/ducks/checkout/types';
import { loadSuccess } from 'store/ducks/checkout/actions';
import { hideMobileOptions, showMobileOptions } from 'store/ducks/nav/actions';
import { Seo } from 'components/standalone';
import { loadCheckout } from 'services/ScriptLoader';
import { RenderIf } from 'components/layout';
import FacebookPixelManagement from 'components/layout/facebookPixelManagement/FacebookPixelManagement';
import UserContext from 'context/user';
import GoogleAdsService from "../../services/GoogleAdsService";
import {
	AddPaymentInfoProps,
	LeadProps, PurchaseSummaryProps
} from "../../shared/interfaces/marketing/props/event-marketing-facebook-props.interface";
import EventService from 'services/EventService';
import { EventMarketingInterface } from 'shared/interfaces/marketing/event-marketing.interface';
import { FacebookPixelContext, GoogleAdsContext, TiktokPixelContext } from "../../context/tracking/declarations";
import { trackFacebookEvent, trackTiktokEvent } from "../../context/tracking/events";
import { EventTrackingDetail } from "../../shared/interfaces/marketing/event-tracking-detail.interface";
import TiktokPixelManagement from "../../components/layout/tiktokPixelManagment/TiktokPixelManagment";

interface StateProps {
	checkout: CheckoutState,
}

const CheckoutPage = () => {
	const { reserve } = useParams();
	const { state }: any = useLocation();
	const { state: facebookPixelState } = useContext(FacebookPixelContext);
	const { state: googleAdsState } = useContext(GoogleAdsContext);
	const { state: tiktokPixelState } = useContext(TiktokPixelContext);
	const { state: userState, setState: setUserState } = useContext(UserContext);
	const [dataMarketing, setDataMarketing] = useState<EventMarketingInterface[] | null>(null);
	const [facebookPixelValid, setFacebookPixelValid] = useState(false);
	const [googleAdsValid, setGoogleAdsValid] = useState(false);
	const [tiktokPixelValid, setTiktokPixelValid] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [hasEventInsights, setHasEventInsights] = useState(false);
	const [geoLocation, setGeoLocation] = useState({ latitude: '', longitude: '' });
	const select = (state: StateProps) => state.checkout;
	const { loading } = useSelector(select);
	const history = useHistory();
	const dispatch = useDispatch();
	const hideLoading = () => loadSuccess();
	let component: any;

	const [showForm, setShowForm] = useState(false);

	const handle = (comp: any) => {
		component = comp;
	}

	const gotoSuccess = (ev: any) => {
		history.push({
			pathname: '/inscricao-sucesso',
			state: { data: ev.detail, hasMarketing: state?.hasMarketing || false },
		})
	}

	useEffect(() => {
		dispatch(hideMobileOptions());
		getLocation();
		getEventMarketingData();
	}, []);

	useEffect(() => {
		if (!userState.isLogged) {
			setIsModalOpen(() => true);
		} else {
			validateInsightsFlag(hasEventInsights);
		}
	}, [userState]);

	const returnToEvent = (ev: any) => {
		history.goBack();
	}

	useEffect(() => {
		loadCheckout(() => dispatch(hideLoading()));

		component.addEventListener('redirect', returnToEvent);
		component.addEventListener('success', gotoSuccess);
		component.addEventListener('sendInsightsFlag', saveEventInsightParams);
	}, []);

	useEffect(() => {
		return () => {
			dispatch(showMobileOptions())
		}
	}, []);

	const getEventMarketingData = async () => {
		if (!state) {
			return
		}
		try {
			const response = await EventService.getMarketingData(state?.eventId);
			setDataMarketing(Array.isArray(response.data) ? response.data : null);
		} catch (e) {
		}
	}

	const triggerInitiateCheckoutEvent = (ev: any) => {
		if (facebookPixelState.active && facebookPixelState.eventInitiateCheckout) {
			trackFacebookEvent('InitiateCheckout', {
				num_items: ev.detail.quantity,
				contents: ev.detail.items,
				...(ev.detail.value > 0 && { value: ev.detail.value }),
				...(ev.detail.value > 0 && { currency: process.env.REACT_APP_CURRENCY })
			});
		}
		if (tiktokPixelState.active && tiktokPixelState.eventInitiateCheckout) {
			trackTiktokEvent('InitiateCheckout', {
				contents: ev.detail.items.map((it: EventTrackingDetail) => {
					return {
						content_id: it.id,
						content_type: "product",
						content_name: it.name,
						quantity: it.quantity,
						price: it.value,
					}
				}),
				...(ev.detail.value > 0 && { value: ev.detail.value }),
				...(ev.detail.value > 0 && { currency: process.env.REACT_APP_CURRENCY })
			});
		}
	}

	const triggerPurchaseEvent = (ev: any) => {
		const eventData = ev.detail as PurchaseSummaryProps;
		if (facebookPixelState.active && facebookPixelState.eventCheckout) {
			trackFacebookEvent('Purchase', {
				num_items: eventData.quantity,
				contents: eventData.items,
				...(eventData.value > 0 && { value: eventData.value }),
				...(eventData.value > 0 && { currency: process.env.REACT_APP_CURRENCY })
			});
		}
		if (googleAdsState.active && googleAdsState.conversionLabel) {
			GoogleAdsService.sendConversionEvent({
				conversionId: googleAdsState.conversionId,
				conversionLabel: googleAdsState.conversionLabel,
				value: eventData.value,
				transactionId: reserve,
			})
		}
		if (tiktokPixelState.active && tiktokPixelState.eventInitiateCheckout) {
			trackTiktokEvent('Purchase', {
				contents: eventData.items.map((it: EventTrackingDetail) => {
					return {
						content_id: it.id,
						content_type: "product",
						content_name: it.name,
						quantity: it.quantity,
						price: it.value,
					}
				}),
				value: eventData.value ?? 0.00,
				currency: process.env.REACT_APP_CURRENCY
			})
		}
	}

	const triggerAddPaymentInfo = (ev: any) => {
		const eventData = ev.detail as AddPaymentInfoProps;
		if (facebookPixelState.active && facebookPixelState.eventAddPaymentInfo) {
			trackFacebookEvent('AddPaymentInfo', {
				num_items: eventData.purchaseSummary.quantity,
				contents: eventData.purchaseSummary.items,
				...(eventData.purchaseSummary.value > 0 && { value: eventData.purchaseSummary.value }),
				...(eventData.purchaseSummary.value > 0 && { currency: process.env.REACT_APP_CURRENCY })
			});
		}
		if (tiktokPixelState.active && tiktokPixelState.eventInitiateCheckout) {
			trackTiktokEvent('AddPaymentInfo', {
				contents: eventData.purchaseSummary.items.map((it: EventTrackingDetail) => {
					return {
						content_id: it.id,
						content_type: "product",
						content_name: it.name,
						quantity: it.quantity,
						price: it.value,
					}
				}),
				...(eventData.purchaseSummary.value > 0 && { value: eventData.purchaseSummary.value }),
				...(eventData.purchaseSummary.value > 0 && { currency: process.env.REACT_APP_CURRENCY })
			});
		}
	}

	const triggerLead = (ev: any) => {
		const eventData = ev.detail as LeadProps;
		if (facebookPixelState.active && facebookPixelState.eventLead) {
			trackFacebookEvent('Lead', {
				num_items: eventData.purchaseSummary.quantity,
				contents: eventData.purchaseSummary.items,
				em: eventData.userEmail,
				...(eventData.purchaseSummary.value > 0 && { value: eventData.purchaseSummary.value }),
				...(eventData.purchaseSummary.value > 0 && { currency: process.env.REACT_APP_CURRENCY })
			});
		}
		if (tiktokPixelState.active && tiktokPixelState.eventInitiateCheckout) {
			if (window.ttq && eventData.userEmail) {
				window.ttq.identify({
					email: eventData.userEmail,
				});
			}
			trackTiktokEvent('CompleteRegistration', {
				contents: eventData.purchaseSummary.items.map((it: EventTrackingDetail) => {
					return {
						content_id: it.id,
						content_type: "product",
						content_name: it.name,
						quantity: it.quantity,
						price: it.value,
					}
				}),
				...(eventData.purchaseSummary.value > 0 && { value: eventData.purchaseSummary.value }),
				...(eventData.purchaseSummary.value > 0 && { currency: process.env.REACT_APP_CURRENCY })
			});
		}
	}


	useEffect(() => {
		const shouldEnableFacebookPixel = facebookPixelState.active && !facebookPixelValid;
		const shouldEnableTiktokPixel = tiktokPixelState.active && !tiktokPixelValid;
		const shouldEnableGoogleAds = googleAdsState.active && !googleAdsValid;
		const shouldAttachEvents = (shouldEnableFacebookPixel || shouldEnableGoogleAds || shouldEnableTiktokPixel);

		if (shouldEnableFacebookPixel) {
			setFacebookPixelValid(true);
			component.addEventListener('initCheckout', triggerInitiateCheckoutEvent);
		}
		if (shouldEnableGoogleAds) {
			setGoogleAdsValid(true);
		}
		if (shouldEnableTiktokPixel) {
			setTiktokPixelValid(true);
		}
		if (shouldAttachEvents) {
			component.addEventListener('lead', triggerLead);
			component.addEventListener('addPaymentInfo', triggerAddPaymentInfo);
			component.addEventListener('concludeCheckout', triggerPurchaseEvent);
		}
	}, [facebookPixelState.active, facebookPixelValid, googleAdsState.active, googleAdsValid,
	tiktokPixelState.active, tiktokPixelValid]);


	const getLocation = () => {
		if (navigator.geolocation) {
			navigator.geolocation.getCurrentPosition((pos) => {
				setGeoLocation({
					latitude: pos.coords.latitude.toString(),
					longitude: pos.coords.longitude.toString(),
				})
			}, (e) => {
				// TODO enviar para o back o motivo
			});
			return;
		}
		// TODO enviar para o back que não tem suporte
	}

	const saveEventInsightParams = (ev: any) => {
		setHasEventInsights(() => ev.detail);

		if (ev.detail && userState.isLogged) {
			validateInsightsFlag(ev.detail);
		}
	}

	const validateInsightsFlag = (eventInsightsEnabled: boolean) => {
		const isDataCompleted = JSON.parse(localStorage.getItem('IS_DATA_COMPLETED') as string);
		if (eventInsightsEnabled && !isDataCompleted) {
			setShowForm(true);
		}
	}

	const updateUserData = () => {
		// AuthService.getUserData(AuthService.getAccessToken())
		// 	.then(({ data }: any) => {
		// 		AuthService.setUserData(data);
		// 		setUserState({
		// 			isLogged: true,
		// 			firstName: data.firstName,
		// 			lastName: data.lastName,
		// 			photoUrl: data.photoUrl,
		// 			username: data.username,
		// 			isDataCompleted: true, // TODO temporário precisa atualizar pelo me
		// 		});
		// 	})
		// 	.catch((err: any) => console.log(err));

		toast('Dados completados com sucesso.', { type: toast.TYPE.SUCCESS })
		setUserState({
			...userState,
			isDataCompleted: true
		});
		localStorage.setItem('IS_DATA_COMPLETED', 'true');
		setShowForm(false);
	}

	return (
		<>
			<Seo title={`${process.env.REACT_APP_NAME} - Finalização de compra`} />
			<RenderIf condition={dataMarketing !== null}>
				<FacebookPixelManagement data={dataMarketing} eventId={state?.id} />
				<TiktokPixelManagement data={dataMarketing} eventId={state?.id} />
			</RenderIf>

			<RenderIf condition={loading}>
				<Loading text="Aguarde um momento, estamos gerando o seu pedido." />
			</RenderIf>

			<RenderIf condition={reserve}>
				<io-checkout
					reserve={reserve}
					tenant={process.env.REACT_APP_TENANT_ID}
					user={JSON.stringify(userState)}
					color={process.env.REACT_APP_COLOR_MAIN}
					geolocation={JSON.stringify(geoLocation)}
					ref={handle}
				/>
			</RenderIf>

			<ModalAuth
				reserve={reserve}
				needAuth={!userState.isLogged}
				isModalOpen={isModalOpen}
				closeModalFn={() => setIsModalOpen(() => false)}
			/>

			<CompleteUserForm isOpen={showForm} showClose={false} successFn={updateUserData} />
		</>
	);
}

export default CheckoutPage;
